import React, { useState, useMemo, useEffect } from 'react';
import { User, X, Shuffle, Plus } from 'lucide-react';
import type { Player } from '../../types';
import { InjuryBadge } from '../ui/InjuryBadge';
import type { InjuryStatus } from '../../constants/injuries';

interface DepthChartEntry {
  id?: string;
  player_id: string | null;
  position: string; // P, C, 1B, 2B, 3B, SS, LF, CF, RF
  depth: number; // Inning number
  color?: string;
}

interface BaseballDiamondFieldingViewProps {
  players: Player[];
  depthChart: DepthChartEntry[];
  activeTab?: string; // Optional, kept for compatibility but not used in baseball
  currentInning: number;
  onPlayerChange: (position: string, inning: number, playerId: string | null) => void;
  playerSortBy: 'firstName' | 'lastName' | 'number';
  onRandomizeClick?: () => void;
  totalInnings: number;
  onAddInning?: () => void;
}

// Position coordinates on a baseball diamond (as percentages of container)
// These are absolute positions that will be positioned via CSS
// Coordinates assume home plate at bottom center, diamond extending upward

// Mobile coordinates (optimized for mobile view)
const POSITION_COORDS_MOBILE: Record<string, { left: string; top: string; label: string }> = {
  P: { left: '50.5%', top: '55%', label: 'P' },        // Pitcher's mound - center of diamond
  C: { left: '50%', top: '90%', label: 'C' },        // Catcher - behind home plate
  '1B': { left: '95%', top: '52%', label: '1B' },    // First base - right side
  '2B': { left: '70%', top: '30%', label: '2B' },    // Second base - top of diamond, shifted right
  '3B': { left: '5%', top: '52%', label: '3B' },    // Third base - left side
  SS: { left: '32%', top: '30%', label: 'SS' },      // Shortstop - between 2B and 3B
  LF: { left: '8%', top: '18%', label: 'LF' },      // Left field - left outfield
  CF: { left: '50%', top: '5%', label: 'CF' },        // Center field - deep center
  RF: { left: '87%', top: '18%', label: 'RF' },      // Right field - right outfield
};

// Desktop/Tablet coordinates (optimized for larger screens)
const POSITION_COORDS_DESKTOP: Record<string, { left: string; top: string; label: string }> = {
  P: { left: '50%', top: '55%', label: 'P' },        // Pitcher's mound - center of diamond
  C: { left: '50%', top: '90%', label: 'C' },        // Catcher - behind home plate
  '1B': { left: '75%', top: '45%', label: '1B' },    // First base - right side
  '2B': { left: '60%', top: '30%', label: '2B' },    // Second base - top of diamond, shifted right
  '3B': { left: '25%', top: '45%', label: '3B' },    // Third base - left side
  SS: { left: '40%', top: '30%', label: 'SS' },      // Shortstop - between 2B and 3B
  LF: { left: '25%', top: '20%', label: 'LF' },      // Left field - left outfield
  CF: { left: '50%', top: '5%', label: 'CF' },        // Center field - deep center
  RF: { left: '75%', top: '20%', label: 'RF' },      // Right field - right outfield
};

export const BaseballDiamondFieldingView: React.FC<BaseballDiamondFieldingViewProps> = ({
  players,
  depthChart,
  currentInning,
  onPlayerChange,
  playerSortBy,
  onRandomizeClick,
  totalInnings,
  onAddInning
}) => {
  const [selectedPosition, setSelectedPosition] = useState<string | null>(null);
  const [showPlayerModal, setShowPlayerModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // 768px is typically the breakpoint for tablet/desktop
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Select coordinates based on screen size
  const POSITION_COORDS = isMobile ? POSITION_COORDS_MOBILE : POSITION_COORDS_DESKTOP;

  // Sort players for dropdown
  const sortedPlayers = useMemo(() => {
    return [...players].sort((a, b) => {
      switch (playerSortBy) {
        case 'firstName':
          return a.first_name.localeCompare(b.first_name);
        case 'lastName':
          return a.last_name.localeCompare(b.last_name);
        case 'number':
        default:
          return (a.number || 0) - (b.number || 0);
      }
    });
  }, [players, playerSortBy]);

  // Get player assigned to a position for current inning
  const getPlayerForPosition = (position: string): Player | null => {
    const entry = depthChart.find(
      e => e.position === position && e.depth === currentInning
    );
    if (!entry?.player_id) return null;
    return players.find(p => p.id === entry.player_id) || null;
  };

  // Get players already assigned this inning with their positions
  const playersInInning = useMemo(() => {
    const playerPositions = new Map<string, string>();
    depthChart
      .filter(e => e.depth === currentInning && e.player_id)
      .forEach(e => {
        if (e.player_id) {
          playerPositions.set(e.player_id, e.position);
        }
      });
    return playerPositions;
  }, [depthChart, currentInning]);

  // Get position name for display
  const getPositionLabel = (position: string): string => {
    return POSITION_COORDS[position]?.label || position;
  };

  // Convert inning number to ordinal (1st, 2nd, 3rd, etc.)
  const getInningOrdinal = (inning: number): string => {
    const lastDigit = inning % 10;
    const lastTwoDigits = inning % 100;
    
    if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
      return `${inning}th`;
    }
    
    switch (lastDigit) {
      case 1: return `${inning}st`;
      case 2: return `${inning}nd`;
      case 3: return `${inning}rd`;
      default: return `${inning}th`;
    }
  };

  const handlePositionClick = (position: string) => {
    setSelectedPosition(position);
    setShowPlayerModal(true);
  };

  const handlePlayerSelect = (playerId: string | null) => {
    if (selectedPosition) {
      // If selecting a player who is already assigned elsewhere, we need to:
      // 1. Clear their old position
      // 2. Assign them to the new position
      const currentPosition = playerId ? playersInInning.get(playerId) : null;
      
      if (currentPosition && currentPosition !== selectedPosition) {
        // Clear the old position first
        onPlayerChange(currentPosition, currentInning, null);
      }
      
      // Assign to new position (or clear if playerId is null)
      onPlayerChange(selectedPosition, currentInning, playerId);
    }
    setShowPlayerModal(false);
    setSelectedPosition(null);
  };

  return (
    <div className="space-y-4">
      {/* Header with title and buttons */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg sm:text-xl font-semibold">{getInningOrdinal(currentInning)} Inning</h3>
        <div className="flex gap-2">
          {onRandomizeClick && (
            <button
              onClick={onRandomizeClick}
              className="btn-secondary flex items-center gap-1.5 px-3 py-1.5 text-xs"
              title="Randomize fielding positions across all innings"
            >
              <Shuffle className="w-3 h-3" />
              Randomize
            </button>
          )}
          {onAddInning && (
            <button
              onClick={onAddInning}
              className="btn-secondary flex items-center gap-1.5 px-3 py-1.5 text-xs"
              title={`Add extra inning (currently ${totalInnings} innings)`}
            >
              <Plus className="w-3 h-3" />
              Add Inning ({totalInnings})
            </button>
          )}
        </div>
      </div>

      {/* Instruction message above the image */}
      <p className="text-sm sm:text-base text-gray-600 text-center mb-3">
        Click on a position on the diamond to assign a player.
      </p>

      {/* Baseball Diamond - Container with background image */}
      <div 
        className="relative w-full max-w-5xl mx-auto bg-center bg-no-repeat overflow-hidden"
        style={{ 
          height: 'clamp(480px, 45vh, 495px)',
          backgroundImage: 'url(/assets/images/baseball-field.jpg)',
          ...(isMobile ? {} : { backgroundSize: '100% 100%' }),
        }}
      >
        {/* Position buttons - absolutely positioned over the field */}
        {Object.entries(POSITION_COORDS).map(([position, coords]) => {
          const assignedPlayer = getPlayerForPosition(position);
          
          return (
            <button
              key={position}
              type="button"
              onClick={() => handlePositionClick(position)}
              className="absolute transform -translate-x-1/2 -translate-y-1/2 group z-10"
              style={{
                left: coords.left,
                top: coords.top,
                maxWidth: '12%',
              }}
            >
              <div
                className={`
                  w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 flex flex-col items-center justify-center
                  transition-all cursor-pointer
                  ${assignedPlayer
                    ? 'bg-primary text-white border-primary shadow-lg ring-1 ring-white'
                    : 'bg-white text-gray-700 border-gray-400 hover:border-primary hover:bg-primary hover:text-white hover:shadow-lg ring-1 ring-white'
                  }
                `}
              >
                {assignedPlayer ? (
                  <>
                    <span className="text-[8px] sm:text-[9px] font-bold leading-tight text-center px-0.5 truncate w-full">
                      {assignedPlayer.last_name}
                    </span>
                    {(assignedPlayer.number || coords.label) && (
                      <span className="text-[7px] sm:text-[8px] font-semibold leading-tight">
                        {assignedPlayer.number ? `#${assignedPlayer.number}` : coords.label}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-[10px] sm:text-xs font-bold">{coords.label}</span>
                )}
              </div>
              {/* Tooltip */}
              {assignedPlayer && (
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 hidden group-hover:block z-20 pointer-events-none">
                  <div className="bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap shadow-lg">
                    {assignedPlayer.first_name} {assignedPlayer.last_name}
                    {assignedPlayer.number && ` (#${assignedPlayer.number})`}
                  </div>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Position assignments list */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
        {Object.keys(POSITION_COORDS).map(position => {
          const assignedPlayer = getPlayerForPosition(position);
          
          return (
            <div
              key={position}
              className="border border-gray-200 rounded-lg p-3 hover:border-primary transition-colors cursor-pointer"
              onClick={() => handlePositionClick(position)}
            >
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-700 shrink-0 pr-5">{position}</span>
                {assignedPlayer ? (
                  <div className="flex items-center gap-2 text-sm min-w-0">
                    {assignedPlayer.number ? (
                      <span className="font-semibold text-primary shrink-0">#{assignedPlayer.number}</span>
                    ) : (
                      <User size={14} className="text-gray-400 flex-shrink-0" />
                    )}
                    <span className="text-gray-700 truncate text-left">
                      {assignedPlayer.first_name} {assignedPlayer.last_name}
                    </span>
                    {assignedPlayer.injury_status && assignedPlayer.injury_status !== 'H' && (
                      <InjuryBadge injuryStatus={assignedPlayer.injury_status as InjuryStatus} size="sm" />
                    )}
                  </div>
                ) : (
                  <span className="text-sm text-gray-400">Click to assign</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Show unassigned players */}
      {players.filter(p => !playersInInning.has(p.id)).length > 0 && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Unassigned Players (Inning {currentInning})</h4>
          <div className="flex flex-wrap gap-2">
            {players
              .filter(p => !playersInInning.has(p.id))
              .map(player => (
                <span
                  key={player.id}
                  className="text-sm text-gray-600 px-2 py-1 bg-white rounded border border-gray-200"
                >
                  {player.number ? `#${player.number} ` : ''}
                  {player.first_name} {player.last_name}
                </span>
              ))}
          </div>
        </div>
      )}

      {/* Player Selection Modal */}
      {showPlayerModal && selectedPosition && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">
                Select Player for {POSITION_COORDS[selectedPosition]?.label}
              </h3>
              <button
                onClick={() => {
                  setShowPlayerModal(false);
                  setSelectedPosition(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-2">
                <button
                  onClick={() => handlePlayerSelect(null)}
                  className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <span className="text-gray-600">Clear Position</span>
                </button>

                {sortedPlayers.map(player => {
                  const currentPosition = playersInInning.get(player.id);
                  const isCurrentlyAtThisPosition = getPlayerForPosition(selectedPosition)?.id === player.id;
                  const isAssignedElsewhere = currentPosition && currentPosition !== selectedPosition;
                  
                  return (
                    <button
                      key={player.id}
                      onClick={() => handlePlayerSelect(player.id)}
                      className={`
                        w-full text-left px-4 py-3 border rounded-lg transition-colors
                        ${isCurrentlyAtThisPosition
                          ? 'bg-primary text-white border-primary'
                          : isAssignedElsewhere
                          ? 'border-yellow-300 bg-yellow-50 hover:bg-primary hover:text-white hover:border-primary'
                          : 'border-gray-200 hover:bg-primary hover:text-white hover:border-primary'
                        }
                      `}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {player.number ? (
                            <span className="font-semibold">#{player.number}</span>
                          ) : (
                            <User size={16} className="text-gray-400" />
                          )}
                          <span>
                            {player.first_name} {player.last_name}
                          </span>
                          {player.injury_status && player.injury_status !== 'H' && (
                            <InjuryBadge injuryStatus={player.injury_status as InjuryStatus} size="sm" />
                          )}
                        </div>
                        {isAssignedElsewhere && (
                          <span className="text-xs font-medium">
                            Currently: {getPositionLabel(currentPosition)}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

